import React, { useState, useEffect } from 'react';
import { gtplSaathiAPI } from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, LogIn, Save, Bot } from 'lucide-react';
import { Panel, Field, Notice, Switch, Pill } from '../components/gtpl/GtplKit';

const READERS = [
  { key: 'ocr', label: 'Local OCR', hint: 'Free, runs on this server. Reads about 6 in 10 captchas, so a login may take a few tries.' },
  { key: 'claude', label: 'Claude', hint: 'Most accurate. Needs an Anthropic API key and costs a fraction of a cent per login.' },
];

// Settings → GTPL Saathi → Automatic login. The password and API key are write-only: never shown after saving.
export default function GtplSaathiAutoLogin({ status, onChanged }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => { setUsername(status?.loginUsername || ''); }, [status?.loginUsername]);

  const reader = status?.captchaReader || 'ocr';

  async function save(body, okMessage) {
    try {
      await gtplSaathiAPI.saveAutoLogin(body);
      if (okMessage) toast.success(okMessage);
      await onChanged();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
      return false;
    }
  }

  async function handleSave() {
    setSaving(true);
    const ok = await save({ loginUsername: username, loginPassword: password, anthropicApiKey: apiKey }, 'Saved');
    if (ok) { setPassword(''); setApiKey(''); }
    setSaving(false);
  }

  async function handleToggle(next) {
    setSwitching(true);
    await save({ autoLogin: next }, next ? 'Automatic login on' : 'Automatic login off');
    setSwitching(false);
  }

  async function handleLoginNow() {
    setLoggingIn(true);
    try {
      const { data } = await gtplSaathiAPI.loginNow();
      if (data.data.ok) toast.success(data.data.message);
      else toast.error(data.data.message || 'Login failed', { duration: 8000 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoggingIn(false);
      onChanged();
    }
  }

  const ready = !!(status?.loginUsername && status?.hasPassword);
  const failed = status?.lastAutoLoginResult === 'failed';

  return (
    <Panel icon={Bot} tone="adv" collapsible open={!status?.autoLogin} title="Automatic login"
      sub="The CRM signs in to GTPL by itself when the session dies"
      right={<Pill tone={status?.autoLogin ? 'ok' : 'mute'} dot>{status?.autoLogin ? 'On' : 'Off'}</Pill>}>
      <p className="gx-hint" style={{ margin: '0 0 14px' }}>
        It reads the captcha and signs in with the username and password below, so nobody has to paste a cookie.
        If the password is rejected it switches itself off instead of retrying, so the account can't get locked.
      </p>

      <div className="gx-form" style={{ maxWidth: 440 }}>
        <Field label="Saathi username">
          <input className="form-control" placeholder="Saathi username" autoComplete="off"
            value={username} onChange={(e) => setUsername(e.target.value)} />
        </Field>
        <Field label="Saathi password">
          <input className="form-control" type="password" autoComplete="new-password"
            placeholder={status?.hasPassword ? 'Password saved — type to replace' : 'Saathi password'}
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>

        <Field label="Captcha reader" hint={READERS.find((r) => r.key === reader)?.hint}>
          <div className="gx-actions">
            {READERS.map((r) => (
              <button key={r.key} type="button" disabled={switching}
                className={`btn btn-sm ${reader === r.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => reader !== r.key && save({ captchaReader: r.key }, `Reader: ${r.label}`)}>
                {r.label}
              </button>
            ))}
          </div>
        </Field>

        {reader === 'claude' && (
          <Field label="Anthropic API key">
            <input className="form-control" type="password" autoComplete="new-password"
              placeholder={status?.hasAnthropicKey ? 'Anthropic API key saved — type to replace' : 'Anthropic API key (sk-ant-…)'}
              value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
          </Field>
        )}

        <div className="gx-actions">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || (!password && !apiKey && username === (status?.loginUsername || ''))}>
            <Save size={15} />{saving ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-adv" onClick={handleLoginNow} disabled={loggingIn || !ready}
            title={ready ? 'Logs in now and saves the new session' : 'Save the username and password first'}>
            <LogIn size={15} />{loggingIn ? 'Logging in… (up to a minute)' : 'Log in now'}
          </button>
        </div>

        <div className="gx-actions" style={{ justifyContent: 'space-between', padding: '10px 0 0', borderTop: '1px solid var(--border)' }}>
          <b>Automatic login {status?.autoLogin ? 'on' : 'off'}</b>
          <Switch checked={!!status?.autoLogin} onChange={handleToggle} disabled={switching || !ready} label="Automatic login" />
        </div>
      </div>

      {status?.lastAutoLoginResult && (
        <div style={{ marginTop: 14 }}>
          <Notice tone={failed ? 'bad' : 'ok'} icon={failed ? XCircle : CheckCircle}>
            {status.lastAutoLoginMessage}
            {status.lastAutoLoginAt && <span style={{ color: 'var(--text-muted)' }}> · {new Date(status.lastAutoLoginAt).toLocaleString('en-GB')}</span>}
          </Notice>
        </div>
      )}
    </Panel>
  );
}
